<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <template v-if="!isBootstrapping">
      <component :is="layout">
        <router-view :key="$route.path" />
      </component>
      <scroll-to-top v-if="enableScrollToTop" />
    </template>
  </div>
</template>
<script>
import ScrollToTop from '@core/components/scroll-to-top/ScrollToTop.vue'

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {

    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,

    ScrollToTop,
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()
    const { enableScrollToTop } = $themeConfig.layout

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()

    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
      enableScrollToTop,
    }
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    isBootstrapping() {
      return this.$auth.loading || this.$tenantCtx.loading
    },
    showGlobalLoader() {
      return this.isBootstrapping || this.loading
    },
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'

      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    loading() {
      return store.getters['app/loading']
    },
  },
  watch: {
    showGlobalLoader: {
      immediate: true,
      handler(isVisible) {
        this.toggleGlobalLoader(isVisible)
      },
    },
  },
  beforeDestroy() {
    this.unlockPageScroll()
    document.getElementById('loading-bg')?.classList.add('loading-bg--hidden')
  },
  mounted() {
    this.initGlobalLoader()
  },
  methods: {
    initGlobalLoader() {
      const loader = document.getElementById('loading-bg')
      if (!loader) return

      loader.classList.remove('loading-bg--start')

      if (this.showGlobalLoader) {
        this.ensureGlobalLoaderBar()
      }
    },
    ensureGlobalLoaderBar() {
      const loader = document.getElementById('loading-bg')
      const container = loader?.querySelector('.loading-container')
      if (!loader || !container || container.querySelector('.loading-bar')) return

      const bar = document.createElement('div')
      bar.className = 'loading-bar'
      bar.innerHTML = '<div class="loading-bar-progress"></div>'
      container.appendChild(bar)

      // Defer bootstrapping class so the bar transition can run from its initial state.
      requestAnimationFrame(() => {
        loader.classList.add('loading-bg--bootstrapping')
      })
    },
    toggleGlobalLoader(isVisible) {
      const loader = document.getElementById('loading-bg')
      if (!loader) return

      if (isVisible) {
        loader.classList.remove('loading-bg--start')
        this.ensureGlobalLoaderBar()
        loader.classList.remove('loading-bg--hidden')
        this.lockPageScroll()
      } else {
        loader.classList.add('loading-bg--hidden')
        this.unlockPageScroll()
      }
    },
    lockPageScroll() {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth

      document.documentElement.style.overflow = 'hidden'
      document.body.style.overflow = 'hidden'

      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }
    },
    unlockPageScroll() {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
    },
  },
  beforeCreate() {
    // Set colors in theme. Fallbacks match bootstrap-extended/_variables.scss —
    // --primary etc. must exist on :root (see theme-css-vars.scss) or ApexCharts
    // receives null/"" and renders gray sparklines with Color() parse errors.
    const colorFallbacks = {
      primary: '#2126cb',
      secondary: '#82868b',
      success: '#28c76f',
      info: '#00cfe8',
      warning: '#ff9f43',
      danger: '#ea5455',
      light: '#f6f6f6',
      dark: '#4b4b4b',
    }
    const colors = Object.keys(colorFallbacks)

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      const fromCss = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
      $themeColors[colors[i]] = fromCss || colorFallbacks[colors[i]]
    }

    // Set Theme Breakpoints
    const breakpointFallbacks = {
      xs: 0, sm: 576, md: 768, lg: 992, xl: 1200,
    }
    const breakpoints = Object.keys(breakpointFallbacks)

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      const raw = useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.trim()
      const parsed = Number(raw.slice(0, -2))
      $themeBreakpoints[breakpoints[i]] = Number.isFinite(parsed)
        ? parsed
        : breakpointFallbacks[breakpoints[i]]
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout

    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
}
</script>
